import { useEffect, useState } from 'react'
import { adminApi } from '../../api/adminApi'
import { User } from '../../types'
import { toast } from 'react-hot-toast'
import { Loader2, UserCheck, UserX, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

export default function AdminUsersPage() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [actionLoading, setActionLoading] = useState<number | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    loadUsers()
  }, [debouncedSearch, page])

  const loadUsers = async () => {
    setLoading(true)
    try {
      const { data } = await adminApi.getUsers({ search: debouncedSearch, page, size: 10 })
      const paged = data.data!
      setUsers(paged.content || [])
      setTotalPages(paged.totalPages)
    } catch {
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleActive = async (user: User) => {
    setActionLoading(user.id)
    try {
      if (user.active) {
        await adminApi.deactivateUser(user.id)
        toast.success('User deactivated')
      } else {
        await adminApi.activateUser(user.id)
        toast.success('User activated')
      }
      loadUsers()
    } catch {
      toast.error('Failed to update user')
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Users</h1>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0) }}
          className="input pl-9"
          placeholder="Search by name, email..."
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 size={24} className="animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="border border-gray-100 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-500">User</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Roles</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Joined</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{user.profile?.displayName || '—'}</p>
                      <p className="text-xs text-gray-400">{user.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {user.roles.map((r) => (
                        <span key={r} className="badge badge-gray text-xs">{r}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                    {formatDistanceToNow(new Date(user.createdAt), { addSuffix: true })}
                  </td>
                  <td className="px-4 py-3">
                    <span className={user.active ? 'badge bg-green-50 text-green-700' : 'badge bg-red-50 text-red-700'}>
                      {user.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleActive(user)}
                      disabled={actionLoading === user.id}
                      className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                      title={user.active ? 'Deactivate' : 'Activate'}
                    >
                      {actionLoading === user.id ? (
                        <Loader2 size={16} className="animate-spin text-gray-400" />
                      ) : user.active ? (
                        <UserX size={16} className="text-red-400" />
                      ) : (
                        <UserCheck size={16} className="text-green-500" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {users.length === 0 && (
            <div className="text-center py-12 text-gray-400">No users found</div>
          )}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="btn-secondary p-2">
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-gray-600">Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="btn-secondary p-2">
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  )
}
